import { useLingui } from '@lingui/react/macro';
import {
  AppstoreOutlined,
  DesktopOutlined,
  PlusOutlined,
  ProjectOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import { routes } from '../lib/routes';
import { useServerStore } from '../stores/useServerStore';
import CreateServerModal from './CreateServerModal';
import UserPanel from './UserPanel';

interface Props {
  onOpenSettings?: (restoreFocus: () => void) => void;
  onLogout?: () => void;
  onLogoutStart?: () => void;
  onLogoutFailure?: () => void;
}

/** Personal resources plus the always-visible Server switcher. */
export default function PrimaryNavigationRail({ onOpenSettings, onLogout, onLogoutStart, onLogoutFailure }: Props) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const location = useLocation();
  const guilds = useServerStore((state) => state.guilds);
  const [createOpen, setCreateOpen] = useState(false);
  const createTriggerRef = useRef<HTMLButtonElement>(null);
  const path = location.pathname;
  const activeServerId = /^\/s\/([^/]+)/.exec(path)?.[1] ?? '';
  const select = (target: string) => {
    if (runUnsavedGuards(undefined, i18n)) navigate(target);
  };

  return (
    <nav className="primary-navigation-rail personal-rail" aria-label={t`Personal navigation`}>
      <div className="personal-rail-profile">
        <UserPanel
          compact
          onOpenSettings={onOpenSettings}
          onLogout={onLogout}
          onLogoutStart={onLogoutStart}
          onLogoutFailure={onLogoutFailure}
        />
      </div>
      <div
        className="primary-navigation-rail-nav personal-rail-nav"
        aria-label={t`Personal resources`}
      >
        <button
          className={`primary-navigation-rail-button${path === '/me' || path === '/me/' ? ' primary-navigation-rail-button-active' : ''}`}
          title={t`Overview`}
          aria-label={t`Overview`}
          onClick={() => select(routes.overview())}
        >
          <AppstoreOutlined aria-hidden="true" />
        </button>
        <button
          className={`primary-navigation-rail-button${path.startsWith('/me/tasks') ? ' primary-navigation-rail-button-active' : ''}`}
          title={t`My Tasks`}
          aria-label={t`My Tasks`}
          onClick={() => select(routes.myTasks())}
        >
          <ProjectOutlined aria-hidden="true" />
        </button>
      </div>
      <div className="personal-rail-divider" aria-hidden="true" />
      <div className="server-list" aria-label={t`Projects`}>
        {guilds.map((guild) => (
          <Tooltip key={guild.id} title={guild.name} placement="right">
            <Button
              type="text"
              shape="circle"
              className={`server-button${guild.id === activeServerId ? ' server-button-active' : ''}`}
              aria-label={t`Switch to ${guild.name}`}
              aria-current={guild.id === activeServerId ? 'page' : undefined}
              onClick={() => guild.id !== activeServerId && select(routes.server(guild.id))}
            >
              {guild.icon || guild.name.trim().charAt(0).toUpperCase() || 'S'}
            </Button>
          </Tooltip>
        ))}
        <Tooltip title={t`Create Project`} placement="right">
          <Button
            ref={createTriggerRef}
            type="text"
            shape="circle"
            className="server-button server-add"
            aria-label={t`Create Project`}
            icon={<PlusOutlined aria-hidden="true" />}
            onClick={() => setCreateOpen(true)}
          />
        </Tooltip>
      </div>
      <div className="personal-rail-utilities">
        <div className="personal-rail-divider" aria-hidden="true" />
        <div className="primary-navigation-rail-nav">
          <button
            className={`primary-navigation-rail-button${path.startsWith('/me/agents') ? ' primary-navigation-rail-button-active' : ''}`}
            title={t`My Agents`}
            aria-label={t`My Agents`}
            onClick={() => select(routes.agents())}
          >
            <RobotOutlined aria-hidden="true" />
          </button>
          <button
            className={`primary-navigation-rail-button${path.startsWith('/me/computers') ? ' primary-navigation-rail-button-active' : ''}`}
            title={t`My Computers`}
            aria-label={t`My Computers`}
            onClick={() => select(routes.computers())}
          >
            <DesktopOutlined aria-hidden="true" />
          </button>
        </div>
      </div>
      {createOpen && (
        <CreateServerModal
          onClose={() => setCreateOpen(false)}
          restoreFocus={() => createTriggerRef.current?.focus()}
        />
      )}
    </nav>
  );
}
